import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";

const useSessionWithRetry = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  const MAX_RETRIES = 10; // Increased from 5
  const RETRY_DELAY_MS = 1000; // Decreased from 2000 for faster retries

  useEffect(() => {
    let isCancelled = false;
    let timeoutId: NodeJS.Timeout;

    const checkSession = async () => {
      try {
        // First try to get the session from storage
        const { data, error } = await supabase.auth.getSession();
        console.log("Session check attempt:", retryCount + 1, data, error);

        if (data.session) {
          if (!isCancelled) {
            console.log("Session found:", data.session.user.id);
            setSession(data.session);
            setLoading(false);
          }
        } else if (retryCount < MAX_RETRIES) {
          setRetryCount(prev => prev + 1);
          console.warn(`Session not ready. Retrying... (${retryCount + 1}/${MAX_RETRIES})`);
          timeoutId = setTimeout(checkSession, RETRY_DELAY_MS);
        } else {
          console.log("Session restoration completed without session");
          if (!isCancelled) {
            setLoading(false);
          }
        }
      } catch (error) {
        console.error("Session check error:", error);
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log("Auth state changed:", _event, session?.user?.id);
      if (session) {
        setSession(session);
        setLoading(false);
      }
    });

    checkSession();

    return () => {
      isCancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      subscription.unsubscribe();
    };
  }, [retryCount]);

  return { session, loading };
};

export default useSessionWithRetry;
